import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionMargins,
  visuallyHidden,
} from '../styles';
import { Container, Heading, HtmlContent, Video } from './ui';
import ServiceCta from './ServiceCta';

const StyledServices = styled.section`
  ${sectionMargins()};

  ${minBreakpointQuery.small`
    overflow: hidden;
  `}
`;

const StyledHeader = styled.header`
  margin: 0 auto 60px;
  max-width: 860px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-bottom: 80px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 100px;
  `}
`;

const StyledText = styled(HtmlContent)`
  margin-top: 20px;

  p {
    ${minBreakpointQuery.small`
      ${fontSize(18)};
    `}

    ${minBreakpointQuery.large`
      ${fontSize(20)};
    `}
  }
`;

const StyledVideo = styled(Video)`
  margin-top: 22px;
`;

const StyledItems = styled.ul`
  display: grid;
  gap: 40px;

  ${minBreakpointQuery.medium`
    gap: 30px;
  `}

  ${minBreakpointQuery.mlarge`
    gap: 20px;
  `}

  ${minBreakpointQuery.large`
    gap: 10px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 0;
  `}
`;

// moved up for nth-child
const StyledCtaWrapper = styled.div`
  ${minBreakpointQuery.small`
    max-width: 400px;
  `}

  ${minBreakpointQuery.medium`
    position: relative;
  `}
`;

// moved up for nth-child
const StyledImage = styled.img`
  ${maxBreakpointQuery.medium`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.medium`
    position: absolute;
    top: 0;
    left: 0;
    height: 240px;
    width: 320px;
    transform: translate(0, -100%);
  `}

  ${minBreakpointQuery.mlarge`
    transform: translate(-10%, -90%);
  `}

  ${minBreakpointQuery.large`
    height: 300px;
    width: 400px;
    transform: translate(-20%, -80%);
  `}

  ${minBreakpointQuery.xlarge`
    transform: translate(-30%, -70%);
  `}

  ${minBreakpointQuery.xxlarge`
    transform: translate(-40%, -60%);
  `}
`;

const StyledItem = styled.li`
  ${minBreakpointQuery.small`
    position: relative;
  `}

  &:first-child {
    ${StyledImage} {
      ${visuallyHidden()};
    }
  }

  &:nth-child(2n) {
    ${StyledCtaWrapper} {
      ${minBreakpointQuery.small`
        margin-left: auto;
      `}
    }

    ${StyledImage} {
      ${minBreakpointQuery.large`
        transform: translate(-10%, -80%);
      `}

      ${minBreakpointQuery.xlarge`
        transform: translate(-10%, -70%);
      `}

      ${minBreakpointQuery.xxlarge`
        transform: translate(-10%, -60%);
      `}
    }
  }

  &:not(:first-child) {
    ${minBreakpointQuery.mlarge`
      margin-top: -20px;
    `}

    ${minBreakpointQuery.large`
      margin-top: -40px;
    `}

    ${minBreakpointQuery.xlarge`
      margin-top: -60px;
    `}

    ${minBreakpointQuery.xxlarge`
      margin-top: -80px;
    `}
  }

  &:not(:last-child) {
    &:before {
      ${minBreakpointQuery.small`
        content: '';
        position: absolute;
        bottom: -30px;
        left: 0;
        display: block;
        height: 1px;
        width: 50%;
        border-bottom: 1px dashed ${brandColours.secondary};
      `}
    }

    &:nth-child(odd) {
      &:before {
        ${minBreakpointQuery.small`
          transform: translateX(50%) rotate(25deg);
        `}
      }
    }

    &:nth-child(even) {
      &:before {
        ${minBreakpointQuery.small`
          transform: translateX(50%) rotate(-25deg);
        `}
      }
    }
  }
`;

const Services = ({ heading, text, video }) => {
  const {
    allDatoCmsService: { nodes },
  } = useStaticQuery(graphql`
    query ServicesQuery {
      allDatoCmsService(sort: { position: ASC }) {
        nodes {
          title
          icon {
            url
            alt
          }
          colour {
            hex
          }
          image: ctaImage {
            url
            alt
          }
          summary {
            value
          }
          ...LinkFragment
        }
      }
    }
  `);

  return (
    <StyledServices>
      <Container narrow={true}>
        <StyledHeader>
          <Heading>{heading}</Heading>
          <StyledText content={text} />
          {video && <StyledVideo data={video} />}
        </StyledHeader>
        <StyledItems>
          {nodes.map((node, id) => {
            const { title, icon, colour, image, summary } = node;

            return (
              <StyledItem key={id}>
                <StyledCtaWrapper>
                  {image && (
                    <StyledImage
                      src={image.url}
                      alt={image.alt}
                      loading="lazy"
                    />
                  )}
                  <ServiceCta
                    heading={title}
                    icon={icon}
                    colour={colour}
                    image={image}
                    text={summary}
                    page={node}
                  />
                </StyledCtaWrapper>
              </StyledItem>
            );
          })}
        </StyledItems>
      </Container>
    </StyledServices>
  );
};

export default Services;
